import React, { useState, Component } from 'react';
import { Plus, X, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { sources as initialSources } from '../lib/data';
function getTierColor(tierId: number | string) {
  switch (tierId.toString()) {
    case '1':
      return 'bg-tier-1 text-white';
    case '2':
      return 'bg-tier-2 text-white';
    case '3':
      return 'bg-tier-3 text-white';
    case 'specialized':
      return 'bg-tier-special text-white';
    default:
      return 'bg-muted text-foreground';
  }
}
type LocalSource = {
  id: string;
  name: string;
  tier: string | number | null;
  logo: string;
};
type LocalTier = {
  id: string | number;
  name: string;
  isCustom: boolean;
};
export function TierManagement() {
  const [sources, setSources] = useState<LocalSource[]>(initialSources.map(s => ({
    id: s.name,
    name: s.name,
    tier: s.tier,
    logo: s.logo
  })));
  const [tiers, setTiers] = useState<LocalTier[]>([{
    id: 1,
    name: 'Tier 1',
    isCustom: false
  }, {
    id: 2,
    name: 'Tier 2',
    isCustom: false
  }, {
    id: 3,
    name: 'Tier 3',
    isCustom: false
  }, {
    id: 'specialized',
    name: 'Specializzata',
    isCustom: false
  }]);
  const [isAddSourceOpen, setIsAddSourceOpen] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const [isAddTierOpen, setIsAddTierOpen] = useState(false);
  const [newTierName, setNewTierName] = useState('');
  const [filter, setFilter] = useState<string | number | 'all' | 'unassigned'>('all');
  const unassignedSources = sources.filter(s => s.tier === null);
  const handleAddSource = () => {
    if (!newSourceName.trim()) return;
    const newSource: LocalSource = {
      id: newSourceName,
      name: newSourceName,
      tier: null,
      logo: `https://ui-avatars.com/api/?name=${encodeURIComponent(newSourceName)}&background=random&color=fff&size=32&font-size=0.4`
    };
    setSources([...sources, newSource]);
    setNewSourceName('');
    setIsAddSourceOpen(false);
  };
  const handleAddTier = () => {
    if (!newTierName.trim()) return;
    const newTierId = newTierName.toLowerCase().replace(/\s+/g, '-');
    if (tiers.some(t => t.id === newTierId)) return; // prevent duplicates
    const newTier: LocalTier = {
      id: newTierId,
      name: newTierName,
      isCustom: true
    };
    setTiers([...tiers, newTier]);
    setNewTierName('');
    setIsAddTierOpen(false);
  };
  const handleDeleteTier = (tierId: string | number) => {
    // Unassign all sources in this tier
    setSources(sources.map(s => s.tier === tierId ? {
      ...s,
      tier: null
    } : s));
    // Remove tier
    setTiers(tiers.filter(t => t.id !== tierId));
  };
  const handleReassignSource = (sourceId: string, newTierId: string | number | null) => {
    // Convert string numbers back to numbers if needed for default tiers
    let parsedTierId = newTierId;
    if (newTierId === '1' || newTierId === '2' || newTierId === '3') {
      parsedTierId = parseInt(newTierId, 10);
    }
    setSources(sources.map(s => s.id === sourceId ? {
      ...s,
      tier: parsedTierId
    } : s));
  };
  const filteredSources = sources.filter(s => {
    if (filter === 'all') return true;
    if (filter === 'unassigned') return s.tier === null;
    return s.tier === filter;
  });
  return <div className="px-6 py-6 max-w-[1400px] mx-auto w-full space-y-8" data-id="element-1905">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1906">
        <div data-id="element-1907">
          <h1 className="text-2xl md:text-3xl font-normal tracking-tight" data-id="element-1908">
            Gestione Tier
          </h1>
        </div>
        <div className="flex items-center gap-3" data-id="element-1909">
          <Button variant="outline" className="rounded-full h-11 px-6 text-sm" onClick={() => setIsAddTierOpen(true)} data-id="element-1910">
            <Plus className="h-4 w-4 mr-1.5" data-id="element-1911" />
            Nuovo Tier
          </Button>
          <Button className="rounded-full h-11 px-6 text-sm" onClick={() => setIsAddSourceOpen(true)} data-id="element-1912">
            <Plus className="h-4 w-4 mr-1.5" data-id="element-1913" />
            Aggiungi testata
          </Button>
        </div>
      </header>

      <div className="bg-card border border-border rounded-lg overflow-hidden" data-id="element-1914">
        {/* Filter tabs */}
        <div className="flex items-center gap-6 px-6 pt-5 pb-0 text-sm overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-1915">
          <button onClick={() => setFilter('all')} className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${filter === 'all' ? 'border-accent text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-id="element-1916">
            Tutte{' '}
            <span className="text-muted-foreground ml-1" data-id="element-1917">
              ({sources.length})
            </span>
          </button>
          {tiers.map(t => {
          const count = sources.filter(s => s.tier === t.id).length;
          return <button key={t.id} onClick={() => setFilter(t.id)} className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${filter === t.id ? 'border-accent text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-id="element-1918">
                {t.name}{' '}
                <span className="text-muted-foreground ml-1" data-id="element-1919">({count})</span>
              </button>;
        })}
          <button onClick={() => setFilter('unassigned')} className={`pb-3 border-b-2 transition-colors whitespace-nowrap ${filter === 'unassigned' ? 'border-accent text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`} data-id="element-1920">
            Non assegnate{' '}
            <span className="text-muted-foreground ml-1" data-id="element-1921">
              ({unassignedSources.length})
            </span>
          </button>
        </div>

        <div className="border-t border-border" data-id="element-1922" />

        {/* Table */}
        <Table data-id="element-1923">
          <TableHeader data-id="element-1924">
            <TableRow className="hover:bg-transparent" data-id="element-1925">
              <TableHead data-id="element-1926">Testata</TableHead>
              <TableHead className="w-[200px]" data-id="element-1927">Tier attuale</TableHead>
              <TableHead className="w-[250px] text-right" data-id="element-1928">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-id="element-1929">
            {filteredSources.length === 0 ? <TableRow className="hover:bg-transparent" data-id="element-1930">
                <TableCell colSpan={3} className="h-32 text-center text-muted-foreground" data-id="element-1931">
                  Nessuna testata trovata per questo filtro.
                </TableCell>
              </TableRow> : filteredSources.map(source => {
            const currentTier = tiers.find(t => t.id === source.tier);
            return <TableRow key={source.id} className="hover:bg-muted/30 transition-colors" data-id="element-1932">
                    <TableCell data-id="element-1933">
                      <div className="flex items-center gap-3" data-id="element-1934">
                        <img src={source.logo} alt={source.name} className="w-8 h-8 rounded-md flex-shrink-0" data-id="element-1935" />
                        <span className="font-medium" data-id="element-1936">{source.name}</span>
                      </div>
                    </TableCell>
                    <TableCell data-id="element-1937">
                      {currentTier ? <Badge className={`font-normal px-2.5 py-1 text-xs border-0 ${getTierColor(currentTier.id)}`} data-id="element-1938">
                          {currentTier.name}
                        </Badge> : <span className="text-sm text-muted-foreground" data-id="element-1939">
                          Non assegnato
                        </span>}
                    </TableCell>
                    <TableCell className="text-right" data-id="element-1940">
                      <div className="flex items-center justify-end gap-3" data-id="element-1941">
                        <select value={source.tier?.toString() || ''} onChange={e => handleReassignSource(source.id, e.target.value)} className="text-sm bg-transparent border border-border rounded-md px-2 py-1.5 outline-none focus:border-accent w-[140px]" data-id="element-1942">
                          <option value="" disabled data-id="element-1943">
                            Assegna a...
                          </option>
                          {tiers.map(t => <option key={t.id} value={t.id} data-id="element-1944">
                              {t.name}
                            </option>)}
                        </select>
                        <Button variant="ghost" size="icon-sm" onClick={() => handleReassignSource(source.id, null)} className="text-muted-foreground hover:text-destructive" title="Rimuovi dal tier" disabled={source.tier === null} data-id="element-1945">
                          <X className="h-4 w-4" data-id="element-1946" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>;
          })}
          </TableBody>
        </Table>
      </div>

      {/* Add Source Dialog */}
      <Dialog open={isAddSourceOpen} onOpenChange={setIsAddSourceOpen} data-id="element-1947">
        <DialogContent className="sm:max-w-[400px]" data-id="element-1948">
          <DialogHeader data-id="element-1949">
            <DialogTitle data-id="element-1950">Nuova testata</DialogTitle>
            <DialogDescription data-id="element-1951">
              Aggiungi una nuova testata all'elenco. Potrai poi assegnarla a un
              tier.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4" data-id="element-1952">
            <input type="text" placeholder="Nome testata (es. Il Sole 24 Ore)" value={newSourceName} onChange={e => setNewSourceName(e.target.value)} className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-accent" autoFocus data-id="element-1953" />
          </div>
          <div className="flex justify-end gap-2" data-id="element-1954">
            <Button variant="outline" onClick={() => setIsAddSourceOpen(false)} data-id="element-1955">
              Annulla
            </Button>
            <Button onClick={handleAddSource} disabled={!newSourceName.trim()} data-id="element-1956">
              Aggiungi
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Tier Dialog */}
      <Dialog open={isAddTierOpen} onOpenChange={setIsAddTierOpen} data-id="element-1957">
        <DialogContent className="sm:max-w-[400px]" data-id="element-1958">
          <DialogHeader data-id="element-1959">
            <DialogTitle data-id="element-1960">Nuovo Tier</DialogTitle>
            <DialogDescription data-id="element-1961">
              Crea un nuovo raggruppamento per le testate.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4" data-id="element-1962">
            <input type="text" placeholder="Nome tier (es. Stampa Locale)" value={newTierName} onChange={e => setNewTierName(e.target.value)} className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-accent" autoFocus data-id="element-1963" />
          </div>
          <div className="flex justify-end gap-2" data-id="element-1964">
            <Button variant="outline" onClick={() => setIsAddTierOpen(false)} data-id="element-1965">
              Annulla
            </Button>
            <Button onClick={handleAddTier} disabled={!newTierName.trim()} data-id="element-1966">
              Crea Tier
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>;
}